import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useCardano } from "@cardano-foundation/cardano-connect-with-wallet";

import { PositionsNavBar } from "./positions-nav-bar/positions-nav-bar";
import { PositionsHoldsSearchBar } from "./positions-search-bar/positions-holds-search-bar";
import { PositionsHoldsTable } from "./positions-holds-table/positions-holds-table";
import { PositionsHistoryTable } from "./positions-history-table/positions-history-table";
import { PositionsHistorySearchBar } from "./positions-search-bar/positions-history-search-bar";

import { Loader } from "@ui/loader/loader";
import { SwipeToSeeMore } from "@ui/swipe/swipe";

import { POSITIONS_HOSTORY_DATA } from "@/constants/positions-data";
import { useCardanoSerializedHandlers } from "@/providers/cardano-serializer-provider/hooks/use-cardano-serialized-handlers";
import { CARDANO_NETWORK } from "@constants/config";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

export const TABS = {
  holds: "holds",
  history: "history",
} as const;

export type TabValue = typeof TABS[keyof typeof TABS];

const decodeHex = (hex: string) => {
  try {
    let str = '';
    for (let i = 0; i < hex.length; i += 2) {
      const charCode = parseInt(hex.substring(i, i + 2), 16);
      if (charCode === 0) break;
      str += String.fromCharCode(charCode);
    }
    return str;
  } catch {
    return hex;
  }
};

export const PositionsMain = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabValue>(() =>
    (searchParams.get("tab") as TabValue) || TABS.holds
  );
  const { enabledWallet, stakeAddress } = useCardano({ limitNetwork: CARDANO_NETWORK });

  const handleTabChange = (tab: TabValue) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const { data: walletData, isLoading } = useQuery({
    queryKey: ["wallet-positions", stakeAddress],
    enabled: !!stakeAddress,
    queryFn: async () => {
      if (!stakeAddress) {
        throw new Error("No wallet connected");
      }
      
      const res = await fetch(`${API_URL}/wallet-positions?address=${stakeAddress}`);
      if (!res.ok) throw new Error("Network response was not ok");
      const json = await res.json();

      return (json.positions || []).map((p: any) => {
        // Use fund_name from backend if available, otherwise decode from hex
        const fundNameHex = p.fund_name.substring(0, 16);
        const readableFundName = p.fund_metadata?.fund_name || decodeHex(fundNameHex);

        // Assets: prefer registry names, fall back to hex decoding
        const decodedTokens = (p.fund_metadata?.tokens || []).map((tHex: string, idx: number) => {
          if (p.fund_metadata?.token_names?.[idx]) return p.fund_metadata.token_names[idx];
          if (tHex.length <= 56) return "ADA / Primary";
          const assetNameHex = tHex.substring(56);
          const name = decodeHex(assetNameHex);
          return name.trim().length > 0 ? name : "ADA / Primary";
        });

        return {
          ...p.fund_metadata,
          readable_name: readableFundName,
          decoded_tokens: decodedTokens,
          total_amount: p.amount,
          is_position: true
        };
      });
    },
  });

  // Show connect wallet message if no wallet connected
  if (!enabledWallet) {
    return (
      <div className="w-full h-full rounded-lg bg-white dark:bg-gray-900 p-6 flex flex-col gap-5">
        <PositionsNavBar activeTab={activeTab} setActiveTab={handleTabChange} />
        <div className="flex flex-col items-center justify-center py-20 text-gray-500">
          <p className="text-lg mb-4">Connect your wallet to view positions</p>
          <p className="text-sm">Use the wallet button in the top right corner</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full rounded-lg bg-white dark:bg-gray-900 p-6 flex flex-col gap-5">
      <PositionsNavBar activeTab={activeTab} setActiveTab={handleTabChange} />

      {activeTab === TABS.holds ? (
        <PositionsHoldsSearchBar />
      ) : (
        <PositionsHistorySearchBar />
      )}

      {!isLoading ? (
        <div className="w-full overflow-x-auto pb-5">
          {activeTab === TABS.holds ? (
            <PositionsHoldsTable data={walletData || []} />
          ) : (
            <PositionsHistoryTable data={POSITIONS_HOSTORY_DATA} />
          )}
        </div>
      ) : (
        <Loader />
      )}
      <SwipeToSeeMore />
    </div>
  );
};